import { VehicleServiceInfoView } from '@euler/app/flows/common/components/VehicleServiceInfoView';
import { useOrderFlowContext } from '@euler/app/flows/order/OrderFlowContext';
import { useConfirm } from '@euler/functions/ux/useConfirm';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { ServiceSubjectType } from '@euler/model/enum';
import { PendingIssue } from '@euler/model/report';
import { VehicleServiceInfo } from '@euler/services';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useMemo } from 'react';
import type { HistoricalServiceInfoNavParams } from './Screens';

export const HistoricalServiceInfoScreen = wrapNavigatorScreen(
  ({ serviceInfo }: { serviceInfo: VehicleServiceInfo }) => {
    const navigation =
      useNavigation<StackNavigationProp<HistoricalServiceInfoNavParams>>();

    const confirm = useConfirm();

    const { orderDetail$ } = useOrderFlowContext();
    const [orderDetail] = useBehaviorSubject(orderDetail$);
    const addedPendingIssues = useMemo<string[] | undefined>(
      () =>
        orderDetail.serviceSubjects
          ?.filter(
            x =>
              x.type === ServiceSubjectType.Construction &&
              x.pendingIssueId != null,
          )
          .map(x => (x as any).pendingIssueId),
      [orderDetail.serviceSubjects],
    );

    const onTogglePendingIssue = usePersistFn((pendingIssue: PendingIssue) => {
      if (
        orderDetail.serviceSubjects?.some(
          x =>
            x.type === ServiceSubjectType.Construction &&
            x.pendingIssueId === pendingIssue.issueId,
        )
      ) {
        void confirm({
          title: '是否删除该遗留问题对应的施工项目?',
        }).then(res => {
          if (res) {
            orderDetail$.next({
              ...orderDetail$.value,
              serviceSubjects: orderDetail$.value.serviceSubjects?.filter(
                x =>
                  x.type !== ServiceSubjectType.Construction ||
                  x.pendingIssueId !== pendingIssue.issueId,
              ),
            });
          }
        });
      } else {
        navigation.push('_assignMember', {
          title: '项目派工',
          onSelect: member => {
            navigation.goBack();
            orderDetail$.next({
              ...orderDetail$.value,
              serviceSubjects: [
                ...(orderDetail$.value.serviceSubjects ?? []),
                {
                  type: ServiceSubjectType.Construction,
                  name: pendingIssue.name,
                  pendingIssueId: pendingIssue.issueId,
                  assignedTo: {
                    id: member.id,
                    name: member.name,
                  },
                },
              ],
            });
          },
        });
      }
    });

    return (
      <VehicleServiceInfoView
        state={{
          status: 'success',
          result: serviceInfo,
        }}
        addedPendingIssues={addedPendingIssues}
        onTogglePendingIssue={onTogglePendingIssue}
        onViewPendingIssues={serviceRecordId => {
          navigation.push('_pendingIssues', {
            bottomSheet: true,
            pendingIssues:
              serviceInfo.serviceRecords.find(x => x._id === serviceRecordId)
                ?.pendingIssuesV2 ?? [],
          });
        }}
      />
    );
  },
  {
    headerShown: true,
    title: '历史服务信息',
  },
);
